/**
 * Typed API client with error handling and auth token injection.
 */

import type {
  PlacementResponse,
  PlacementCreate,
  PlacementCreateResponse,
  PlacementUpdate,
  PlacementDetailResponse,
  PlacementListResponse,
  ShareUrlRequest,
  ShareUrlResponse,
  DomainResponse,
  DomainCreate,
  DomainStatusResponse,
  DomainListResponse,
  OrganisationResponse,
  OrganisationUpdate,
  OrganisationContext,
  UserInvite,
  TimeSeriesResponse,
  CascadeResponse,
  TopSparksResponse,
  UsageResponse,
  ErrorResponse,
} from './schemas';

export interface ApiError {
  code: string;
  message: string;
  details?: Record<string, unknown> | null;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:10000';
const API_V1_PREFIX = '/v1';

/**
 * Get auth token from Supabase session.
 */
async function getAuthToken(): Promise<string | null> {
  // This will be implemented with Supabase client
  const token = localStorage.getItem('supabase.auth.token');
  return token;
}

/**
 * Get org ID from JWT claims or context.
 */
function getOrgId(): string | null {
  const orgId = localStorage.getItem('org_id');
  return orgId;
}

/**
 * Typed fetch wrapper with error handling.
 */
async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = await getAuthToken();
  const orgId = getOrgId();

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  if (orgId) {
    headers['X-Org-Id'] = orgId;
  }

  headers['X-Client-Version'] = import.meta.env.VITE_APP_VERSION || '0.1.0';

  // Add /v1 prefix for all API endpoints except internal routes
  const fullEndpoint = endpoint.startsWith('/internal') ? endpoint : `${API_V1_PREFIX}${endpoint}`;
  const response = await fetch(`${API_BASE_URL}${fullEndpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    let error: ApiError;
    try {
      const errorData = (await response.json()) as ErrorResponse | { detail: string };
      
      // Handle FastAPI's default error format: {"detail": "error message"}
      if ('detail' in errorData && typeof errorData.detail === 'string') {
        error = {
          code: 'API_ERROR',
          message: errorData.detail,
        };
      } 
      // Handle our custom error format: {"error": {"code": "...", "message": "..."}}
      else if ('error' in errorData && errorData.error) {
        error = errorData.error;
      } 
      // Fallback
      else {
        error = {
          code: 'UNKNOWN_ERROR',
          message: `HTTP ${response.status}: ${response.statusText}`,
        };
      }
    } catch {
      error = {
        code: 'UNKNOWN_ERROR',
        message: `HTTP ${response.status}: ${response.statusText}`,
      };
    }
    throw new Error(error.message);
  }

  // Handle empty responses (204 No Content, etc.)
  // 204 No Content means no response body, so return early
  if (response.status === 204) {
    return {} as T;
  }

  // Check content-length header - if 0, return empty object
  const contentLength = response.headers.get('content-length');
  if (contentLength === '0') {
    return {} as T;
  }

  // Get response text first (we can only read the body once)
  const contentType = response.headers.get('content-type');
  
  // If no content-type or not JSON, return empty object
  if (!contentType || !contentType.includes('application/json')) {
    return {} as T;
  }

  // Read response as text first to check if it's empty
  const text = await response.text();
  
  // If text is empty or whitespace, return empty object
  if (!text || text.trim() === '') {
    return {} as T;
  }

  // Parse JSON from text
  try {
    return JSON.parse(text) as T;
  } catch (parseError) {
    // If JSON parsing fails, return empty object for void responses
    if (parseError instanceof SyntaxError) {
      console.warn(`Failed to parse JSON response from ${endpoint}: ${parseError.message}`);
      return {} as T;
    }
    throw parseError;
  }
}

/**
 * Auth endpoints
 */
export const authApi = {
  /**
   * Get current user and organization context.
   */
  async getMe(): Promise<OrganisationContext> {
    return apiFetch<OrganisationContext>('/orgs/me');
  },
};

/**
 * Organisation endpoints
 */
export const orgApi = {
  /**
   * Get organisation by ID.
   */
  async getOrg(orgId: string): Promise<OrganisationResponse> {
    return apiFetch<OrganisationResponse>(`/orgs/${orgId}`);
  },

  /**
   * Update organisation.
   */
  async updateOrg(orgId: string, data: OrganisationUpdate): Promise<OrganisationResponse> {
    return apiFetch<OrganisationResponse>(`/orgs/${orgId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  /**
   * Invite user to organisation.
   */
  async inviteUser(orgId: string, invite: UserInvite): Promise<void> {
    return apiFetch<void>(`/orgs/${orgId}/invites`, {
      method: 'POST',
      body: JSON.stringify(invite),
    });
  },

  /**
   * Remove user from organisation.
   */
  async removeUser(orgId: string, userId: string): Promise<void> {
    return apiFetch<void>(`/orgs/${orgId}/users/${userId}`, {
      method: 'DELETE',
    });
  },
};

/**
 * Domain endpoints
 */
export const domainApi = {
  /**
   * List domains.
   */
  async listDomains(cursor?: string): Promise<DomainListResponse> {
    const params = new URLSearchParams();
    if (cursor) {
      params.append('cursor', cursor);
    }
    const query = params.toString();
    return apiFetch<DomainListResponse>(`/domains${query ? `?${query}` : ''}`);
  },

  /**
   * Create domain.
   */
  async createDomain(data: DomainCreate): Promise<DomainResponse> {
    return apiFetch<DomainResponse>('/domains', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Get domain status.
   */
  async getDomainStatus(domainId: string): Promise<DomainStatusResponse> {
    return apiFetch<DomainStatusResponse>(`/domains/${domainId}/status`);
  },

  /**
   * Delete domain.
   */
  async deleteDomain(domainId: string): Promise<void> {
    return apiFetch<void>(`/domains/${domainId}`, {
      method: 'DELETE',
    });
  },
};

/**
 * Placement endpoints
 */
export const placementApi = {
  /**
   * List placements.
   */
  async listPlacements(params?: {
    domainId?: string;
    q?: string;
    page?: number;
    cursor?: string;
  }): Promise<PlacementListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.domainId) searchParams.append('domainId', params.domainId);
    if (params?.q) searchParams.append('q', params.q);
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.cursor) searchParams.append('cursor', params.cursor);
    const query = searchParams.toString();
    return apiFetch<PlacementListResponse>(`/placements${query ? `?${query}` : ''}`);
  },

  /**
   * Get placement by ID.
   */
  async getPlacement(placementId: string): Promise<PlacementDetailResponse> {
    return apiFetch<PlacementDetailResponse>(`/placements/${placementId}`);
  },

  /**
   * Create placement.
   */
  async createPlacement(data: PlacementCreate): Promise<PlacementCreateResponse> {
    return apiFetch<PlacementCreateResponse>('/placements', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Update placement.
   */
  async updatePlacement(placementId: string, data: PlacementUpdate): Promise<PlacementResponse> {
    return apiFetch<PlacementResponse>(`/placements/${placementId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  /**
   * Create share URL.
   */
  async createShareUrl(placementId: string, data: ShareUrlRequest): Promise<ShareUrlResponse> {
    return apiFetch<ShareUrlResponse>(`/links/${placementId}/share-url`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

/**
 * Analytics endpoints
 */
export const analyticsApi = {
  /**
   * Get placement time series data.
   */
  async getTimeSeries(
    placementId: string,
    params?: {
      from?: string;
      to?: string;
      interval?: '1h' | '1d' | '1w';
    }
  ): Promise<TimeSeriesResponse> {
    const searchParams = new URLSearchParams();
    if (params?.from) searchParams.append('from', params.from);
    if (params?.to) searchParams.append('to', params.to);
    if (params?.interval) searchParams.append('interval', params.interval);
    const query = searchParams.toString();
    return apiFetch<TimeSeriesResponse>(
      `/placements/${placementId}/timeseries${query ? `?${query}` : ''}`
    );
  },

  /**
   * Get placement cascade.
   */
  async getCascade(placementId: string, window = '24h'): Promise<CascadeResponse> {
    return apiFetch<CascadeResponse>(`/placements/${placementId}/cascade?window=${window}`);
  },

  /**
   * Get top sparks.
   */
  async getTopSparks(params?: {
    from?: string;
    to?: string;
    limit?: number;
  }): Promise<TopSparksResponse> {
    const searchParams = new URLSearchParams();
    if (params?.from) searchParams.append('from', params.from);
    if (params?.to) searchParams.append('to', params.to);
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    const query = searchParams.toString();
    return apiFetch<TopSparksResponse>(`/reports/top-sparks${query ? `?${query}` : ''}`);
  },

  /**
   * Get usage statistics.
   */
  async getUsage(): Promise<UsageResponse> {
    return apiFetch<UsageResponse>('/usage');
  },
};

/**
 * Billing endpoints
 */
export const billingApi = {
  /**
   * Get Stripe customer portal URL.
   */
  async getPortalUrl(): Promise<{ url: string }> {
    return apiFetch<{ url: string }>('/billing/portal', {
      method: 'POST',
    });
  },

  /**
   * Get plans.
   */
  async getPlans(): Promise<Array<{ id: string; price: number; limits: { domains: number; links: number; clicks: number } }>> {
    return apiFetch<Array<{ id: string; price: number; limits: { domains: number; links: number; clicks: number } }>>('/plans');
  },

  /**
   * Create checkout session.
   */
  async createCheckout(plan: string, successUrl: string, cancelUrl: string): Promise<{ url: string }> {
    return apiFetch<{ url: string }>('/billing/checkout', {
      method: 'POST',
      body: JSON.stringify({
        plan,
        success_url: successUrl,
        cancel_url: cancelUrl,
      }),
    });
  },
};

